/**
 * English catalog.
 * Structure is enforced by `Catalog` (derived from `uz.ts`) — a missing key
 * fails the TypeScript build.
 *
 * Interface languages — TZ X4: uz / ru / en.
 */
import type { Catalog } from './ru'

export const en: Catalog = {
  common: {
    appName: 'GROM Admin',
    cancel: 'Cancel',
    confirm: 'Confirm',
    save: 'Save',
    saving: 'Saving…',
    delete: 'Delete',
    close: 'Close',
    retry: 'Retry',
    search: 'Search',
    loading: 'Loading…',
    all: 'All',
    none: 'None',
    copy: 'Copy',
    copied: 'Copied',
    back: 'Back',
    actions: 'Actions',
    reason: 'Reason',
    optional: '(optional)',
    apply: 'Apply',
    clear: 'Clear',
    signOut: 'Sign out',
    profile: 'Profile',
    theme: 'Theme',
    themeDark: 'Dark',
    themeLight: 'Light',
    language: 'Language',
    never: 'Never',
    unknown: 'Unknown',
    yes: 'Yes',
    no: 'No',
    planned: 'planned',
    plannedHint: 'This feature is not available yet',
  },

  auth: {
    signInTitle: 'GROM Admin',
    signInSubtitle: 'Sign in to the admin console',
    username: 'Username',
    password: 'Password',
    signIn: 'Sign in',
    signingIn: 'Signing in…',
    invalidCredentials: 'Invalid username or password',
    networkError: 'Could not reach the server. Check your connection.',
    twoFactorCode: '2FA code',
    twoFactorInvalid: 'Invalid 2FA code',

    ssoTitle: 'Single sign-on (SSO)',
    ssoDesc: 'Sign in through my.casinoplaneta.info. The provider is not connected yet.',
    webauthnTitle: 'Hardware 2FA (WebAuthn)',
    webauthnDesc:
      'A security key will be required. No provider is selected yet — username and password are used for now.',

    sessionExpiredTitle: 'Session expired',
    sessionExpiredDesc: 'Sign in again to continue.',
    sessionExpiredAction: 'Sign in again',

    idleLockedTitle: 'Locked',
    idleLockedDesc: 'No activity for 30 minutes. Enter your password to continue.',
    unlock: 'Unlock',

    roleSelectTitle: 'Select a role',
    roleSelectDesc: 'You have several roles. Pick one for this session.',

    deviceTitle: 'Register this device',
    deviceDesc: 'A new device requires manager approval.',
    deviceFingerprint: 'Device fingerprint',
    devicePending: 'Approval request sent',
    deviceWaiting: 'Waiting for manager approval. You cannot sign in until this device is approved.',

    breakGlassTitle: 'Break-glass access',
    breakGlassDesc:
      'All permissions are unlocked for 4 hours. The whole team is notified immediately and every action is audited separately.',
    breakGlassReason: 'Reason (required)',
    breakGlassReasonHint: 'Why is emergency access needed? This text is sent to the whole team.',
    breakGlassWarning: 'This action has irreversible consequences. Use it only in a real emergency.',
    breakGlassActivate: 'Activate break-glass access',

    ipDeniedTitle: 'IP address not allowed',
    ipDeniedDesc: 'Connect from the office network or the corporate VPN.',
    ipCurrent: 'Your IP address',

    demoTitle: 'A1 — Authentication screens',
    demoDesc:
      'All 8 A1 screens. There is no server flow yet — screens with no live wiring are reviewed and signed off here.',
    demoBackToList: '← Back to the A1 screen list',
    demoLiveNote: 'live (in the sign-in flow)',
    demoOrphanNote: 'written, but no server flow',

    welcomeTitle: 'Welcome',
    welcomeDesc: 'A few steps before you start.',
    welcomeRole: 'Your role',
    welcomeAction: 'Get started',
    welcomeStepRole:
      'Your role defines your permissions — the visible sections adapt to them.',
    welcomeStepSecurity:
      'Keep your account safe: never share your password, and the session locks after 30 minutes of inactivity.',
    welcomeStepTour:
      'The "Modules" section shows the status of every section — live and planned screens are marked openly.',
  },

  modules: {
    overviewDesc: (done: number, total: number) => `28 modules · ${done}/${total} screens live`,
    overviewReady: (n: number) => `${n} modules fully ready`,
    moduleDesc: (done: number, total: number) =>
      `${done}/${total} screens live · rest planned (most await an endpoint)`,
    live: 'live',
    planned: 'planned',
    ready: 'ready',
    skeleton: 'skeleton',
    skeletonCount: (n: number) => `${n} skeleton (waiting on infra)`,
    plannedScreenDesc: 'This screen is not built yet. Structure is ready; going live needs:',
    plannedNeeds: 'Needs:',
    plannedGeneric: 'a server endpoint or infrastructure',
  },

  broadcast: {
    title: 'Broadcasts',
    note: 'Compose a mass announcement and view history (sending is a separate confirmed step).',
    compose: 'New broadcast',
    titleField: 'Title',
    bodyField: 'Message',
    audience: 'Audience',
    all: 'Everyone',
    active: 'Active users',
    staff: 'Staff',
    draft: 'Draft',
    queued: 'Queued',
    sent: 'Sent',
    failed: 'Failed',
    recipients: 'Recipients',
    created: 'Draft saved',
    createFailed: 'Could not save',
    draftNote: 'Saved as a draft — not sent to anyone.',
    saveDraft: 'Save draft',
    card: 'Broadcast card',
    cancelled: 'Cancelled',
    queue: 'Queue',
    cancel: 'Cancel',
    test: 'Test',
    actionOk: 'Done',
    actionFailed: 'Action failed',
    schedule: 'Schedule',
    scheduledTitle: 'Scheduled',
    pushHealth: 'Push health',
    pushHealthNote: 'Push provider status: FCM, registered tokens, breakdown.',
    fcm: 'FCM',
    fcmYes: 'Configured',
    fcmNo: 'Not configured',
    pushTokens: 'Push tokens',
    byProvider: 'By provider',
    statusBreakdown: 'By status',
    templates: 'Templates',
    tplNew: 'New template',
    tplName: 'Template name',
    tplSaved: 'Template saved',
    tplDeleted: 'Template deleted',
    policy: 'Broadcast policy',
    policyNote: 'Rate limiting and anti-spam rules.',
    throttle: 'Per-minute limit',
    throttleHint: '0 = unlimited',
    dailyCap: 'Daily cap',
    dailyCapHint: '0 = unlimited',
    cooldown: 'Cooldown (hours)',
    cooldownHint: 'Min hours between broadcasts to one user',
    antispam: 'Anti-spam enabled',
    settingsSaved: 'Settings saved',
    banner: 'Service banner',
    bannerNote: 'Maintenance/announcement banner shown in the app (admin-set).',
    bannerText: 'Banner text',
    severity: 'Severity',
    info: 'Info',
    warning: 'Warning',
    critical: 'Critical',
    bannerEnabled: 'Banner enabled',
    preview: 'Preview',
  },

  nav: {
    legal: 'Legal',
    verification: 'Verification',
    support: 'Support',
    appeals: 'Appeals',
    dashboard: 'Dashboard',
    modules: 'Modules',
    groups: 'Groups',
    finance: 'Finance',
    bots: 'Bots',
    gifts: 'Gifts',
    gromauktion: 'GromAuktion',
    moderation: 'Moderation',
    reports: 'Reports',
    verificationRequests: 'Verification requests',
    users: 'Users',
    geo: 'Geography',
    staff: 'Staff',
    security: 'Security',
    broadcast: 'Broadcasts',
    auditLog: 'Audit log',
    settings: 'Settings',
    collapse: 'Collapse sidebar',
    expand: 'Expand sidebar',
    menu: 'Menu',
  },

  table: {
    columns: 'Columns',
    density: 'Density',
    densityCompact: 'Compact',
    densityNormal: 'Normal',
    densityComfortable: 'Comfortable',
    filters: 'Filters',
    addFilter: 'Add filter',
    clearFilters: 'Clear filters',
    savedViews: 'Saved views',
    saveCurrentView: 'Save current view',
    manageViews: 'Manage views',
    defaultView: 'Default view',
    export: 'Export',
    refresh: 'Refresh',
    selected: (n: number) => `${n.toLocaleString()} selected`,
    selectAllMatching: (n: number) => `All matching the filter: ${n.toLocaleString()}`,
    clearSelection: 'Clear selection',
    rowsPerPage: 'Per page',
    newRecords: (n: number) => `${n.toLocaleString()} new ↑`,
    showNew: 'Show',
    rangeLabel: (from: number, to: number, total: number) =>
      `${from.toLocaleString()}–${to.toLocaleString()} / ${total.toLocaleString()}`,
    prevPage: 'Previous',
    nextPage: 'Next',
    revealPii: 'Reveal',
    piiRevealed: 'Revealed — this action was written to the audit log',
    shortcuts: 'Keyboard shortcuts',
  },

  filter: {
    contains: 'contains',
    equals: 'equals',
    startsWith: 'starts with',
    gt: 'greater than',
    lt: 'less than',
    between: 'between',
    is: 'is',
    isAny: 'is any of',
    today: 'Today',
    last7: '7 days',
    last30: '30 days',
    thisMonth: 'This month',
    lastMonth: 'Last month',
    custom: 'Custom',
    from: 'from',
    to: 'to',
  },

  savedViews: {
    title: 'Segments',
    empty: 'No saved filters. Set up the filters, then press "Save current view".',
    saveCurrent: 'Save current view',
    namePlaceholder: 'Segment name…',
  },

  states: {
    emptyTitle: 'Nothing found',
    emptyDesc: 'Try changing the filters or clearing the search.',
    errorTitle: 'Could not load',
    errorDesc: 'The request failed.',
    permissionDeniedTitle: 'No access',
    permissionDeniedDesc: (perm: string) => `This section requires the “${perm}” permission.`,
    requestAccess: 'Request access',
    notFoundTitle: 'Page not found',
  },

  danger: {
    impactTitle: 'Impact',
    impactCalculating: 'Calculating impact…',
    reversible: (window: string) => `Reversible within ${window}`,
    irreversible: 'Irreversible',
    reasonLabel: 'Reason',
    reasonRequired: 'Reason (required)',
    reasonPlaceholder: 'Why is this action being taken?',
    reasonTooShort: (min: number) => `Enter at least ${min} characters`,
    reasonPresetLabel: 'Quick reasons',
    typeToConfirmLabel: (name: string) => `Type “${name}” to confirm`,
    typeToConfirmMismatch: 'The text does not match',
    fourEyesTitle: 'Second approver required',
    fourEyesDesc:
      'This action is not executed immediately. A request is created and another staff member must approve it.',
    fourEyesCreate: 'Create request',
    fourEyesPending: 'Awaiting approval',
    waitPeriodTitle: 'Wait period',
    waitPeriodDesc: (min: number) =>
      `The action runs in ${min} minutes. Any security staff member can stop it during that time.`,
    notifyTeam: 'The whole team will be notified',
    undo: 'Undo',
    undoneToast: 'Action undone',
    level: (n: number) => `Level ${n}`,
    levelName: {
      1: 'Low',
      2: 'Moderate',
      3: 'High',
      4: 'Critical',
      5: 'Emergency',
    } as Record<number, string>,
  },

  rt: {
    title: 'Real-time monitor',
    note: 'Live counters — refreshes every 5 seconds.',
    active5m: 'Active (5m)',
    msgMin: 'Messages / min',
    signupMin: 'Signups / min',
    msgHour: 'Messages / hour',
    signupHour: 'Signups / hour',
    series: 'Message flow (last 15 minutes)',
    updated: 'Updated',
    pause: 'Pause',
    resume: 'Resume',
  },

  userExtra: {
    ticketsTitle: 'Support tickets',
    noTickets: 'No tickets',
    priority: 'Priority',
    reportsTitle: 'Reports (by and against)',
    direction: 'Direction',
    by: 'By',
    against: 'Against',
    target: 'Target',
    resolved: 'Resolved',
    dismissed: 'Dismissed',
    openReport: 'Open',
    botsTitle: 'Owned bots',
    botUsername: 'Bot @username',
    botName: 'Bot name',
    revoked: 'Revoked',
    giftsTitle: 'Gifts / Stars',
    giftsSent: 'Sent',
    giftsReceived: 'Received',
    giftsCount: 'Total gifts',
    relatedTitle: 'Related accounts',
    platform: 'Platform',
    device: 'Device',
    lastActive: 'Last active',
  },

  infra: {
    title: 'Infrastructure dashboard',
    note: 'Go process runtime + PostgreSQL health — live, read-only.',
    runtime: 'Runtime (Go)',
    database: 'Database',
    uptime: 'Uptime',
    goroutines: 'Goroutines',
    heap: 'Heap',
    heapAlloc: 'Heap allocated',
    heapSys: 'Heap system',
    dbSize: 'DB size',
    goVersion: 'Go version',
    numCpu: 'CPU cores',
    gcCount: 'GC count',
    pgVersion: 'PostgreSQL',
    dbName: 'DB name',
    migration: 'Migration',
    activeConns: 'Active connections',
    pool: 'Connection pool',
    idle: 'idle',
  },

  myTasks: {
    title: 'My tasks',
    note: 'Work assigned to you',
    myTickets: 'My tickets',
    approvals: 'Awaiting my approval',
    noTickets: 'No open tickets assigned to you',
    noApprovals: 'No four-eyes awaiting approval',
  },

  secDash: {
    title: 'Security dashboard',
    note: 'Audit, four-eyes and staff status — live.',
    fourEyesOpen: 'Open four-eyes',
    staffActive: 'Active staff / total',
    auditWindow: 'Audit events (window)',
    topActions: 'Top actions',
    recentAudit: 'Recent audit',
    fullLog: 'Full log',
  },

  supDash: {
    title: 'Support dashboard',
    note: 'Tickets by status and priority — live.',
    total: 'Total tickets (window)',
    openish: 'Open / pending',
    categories: 'Categories',
    byStatus: 'By status',
    byPriority: 'By priority',
    recent: 'Recent tickets',
  },

  modDash: {
    title: 'Moderation dashboard',
    note: 'Pending workload across all trust & safety queues — live.',
    openWorkload: 'Open workload (total pending)',
    reports: 'Reports',
    tickets: 'Open tickets',
    appeals: 'Appeals',
    verifications: 'Verification',
    legal: 'Legal requests',
  },

  dash: {
    users: 'Users',
    usersHint: 'Deleted accounts are excluded',
    dau: 'Active (24 hours)',
    mau: 'Active (30 days)',
    messagesToday: 'Messages (24 hours)',
    messages7d: 'Messages (7 days)',
    dialogs: 'Dialogs',
    mediaFiles: 'Media files',
    messagesPerDay: 'Messages per day (7 days)',
    activation: 'Activation',
    activationHint: (sent: number, total: number) =>
      `${sent.toLocaleString()} of ${total.toLocaleString()} users sent their first message`,
    registrations: 'Registrations',
    regToday: 'Today',
    regD7: '7 days',
    regD30: '30 days',
    inconsistentTitle: 'Server numbers contradict each other',
    inconsistentDesc: (d7: number, total: number) =>
      `“7 days” (${d7.toLocaleString()}) exceeds total users (${total.toLocaleString()}). ` +
      `Cause: the registration counter includes deleted accounts while the total does not. ` +
      `Treat this number as unreliable until the server is fixed.`,
    noData: 'No data for this period',
    valuesTable: 'Value table',
    tvTitle: 'Screen mode',
    tvLive: 'Live',
    tvOffline: 'Offline',
    tvUpdated: (time: string) => `Updated ${time}`,
    tvExit: 'Exit',
    growthTitle: 'Growth dashboard',
    topCountries: 'Registrations by country',
  },

  groups: {
    group: 'Group',
    channel: 'Channel',
    members: 'Members',
    forum: 'Forum',
    cardTitle: 'Chat card',
    about: 'About',
    owner: 'Owner',
    admin: 'Admin',
    member: 'Member',
    customTitle: 'Custom title',
    joined: 'Joined',
    restricted: 'Restricted',
    membersTitle: 'Members list',
    admins: 'Admins',
    adminsTitle: 'Admins and permissions',
    joinRequests: 'Join requests',
    requestedAt: 'Requested',
    bio: 'Bio',
    topics: 'Forum topics',
    general: 'General',
    pinned: 'Pinned',
    closed: 'Closed',
    messages: 'Message history',
    messageText: 'Text',
    views: 'Views',
    edited: 'Edited',
    deleted: 'Deleted',
    adminLog: 'Admin log',
    actor: 'Actor',
    media: 'Media gallery',
    mediaNote: 'Metadata + blurhash; byte preview awaits media-proxy (follow-up).',
    mime: 'MIME',
    size: 'Size',
    dimensions: 'Dimensions',
    uploader: 'Uploader',
    stats: 'Statistics',
    new7d: 'New members (7d)',
    messages7d: 'Messages (7d)',
    messagesTotal: 'Total messages',
    topPosters: 'Top posters',
    boosts: 'Boost status',
    boostLevel: 'Level',
    boostCount: 'Boosters',
    boostedAt: 'Boosted at',
    boostFree: 'GROM boosts are free (no money).',
    growth: 'Growth chart',
    botsTitle: 'Connected bots',
    settingsTitle: 'Settings',
    reportsTitle: 'Reports',
    signMessages: 'Signed posts',
    slowMode: 'Slow mode',
    joinByRequest: 'Join by request',
    contentProtected: 'Content protected',
    historyVisible: 'History visible',
    forumMode: 'Forum mode',
    topicLayout: 'Topic layout',
  },

  account: {
    secTitle: 'Security & access',
    secIdentity: 'Identity',
    secAccess: 'Access (RBAC)',
    sec2fa: 'Two-factor authentication',
    sec2faNote: 'Setup requires a backend endpoint (planned).',
    sec2faOn: 'Enabled',
    sec2faOff: 'Not enabled',
    sec2faEnable: 'Enable 2FA',
    sec2faDisable: 'Disable',
    sec2faScan: 'Add this secret to your authenticator app (Google Authenticator, etc.), then enter the 6-digit code to confirm.',
    sec2faVerify: 'Verify & enable',
    sec2faCode: '6-digit code',
    sec2faSecret: 'Secret key',
    secDevices: 'Devices & sessions',
    secDevicesNote: 'Session management requires a backend endpoint (planned).',
    backendPending: 'Backend pending',
    notifTitle: 'Notifications',
    notifNote: 'Local browser preferences for admin-panel alerts.',
    notifDesktop: 'Desktop notifications',
    notifSound: 'Sound',
    notifReports: 'New reports',
    notifTickets: 'New tickets',
    notifFourEyes: 'Four-eyes requests',
    profile: 'My profile',
    interface: 'Interface settings',
    shortcuts: 'Keyboard shortcuts',
    username: 'Username',
    role: 'Role',
    roles: 'Roles',
    logout: 'Log out',
    language: 'Language',
    theme: 'Theme',
    dark: 'Dark',
    light: 'Light',
    scNext: 'Next row',
    scPrev: 'Previous row',
    scSelect: 'Select',
    scRange: 'Select range',
    scOpen: 'Open',
    scPanel: 'Side panel',
    scSearch: 'Global search',
    scFilter: 'Focus filter',
    scClose: 'Close / cancel',
    scHelp: 'Shortcuts list',
    help: 'Help & guide',
    helpIntro:
      'GROM admin panel — an operations console: users, groups/channels, finance, geography, analytics and security. Each screen’s spec ID is shown next to the title.',
    helpModules: '614-screen module registry and progress',
    helpShortcuts: 'Keyboard shortcuts list',
  },

  loc: {
    glossaryTitle: 'Glossary',
    glossaryNote: 'Preferred translations of domain terms — keep them consistent across the app.',
    editorTitle: 'Translation editor',
    editorNote: 'Edits are local (browser). Download the patch and commit it — server-side saving needs a write endpoint (planned).',
    downloadPatch: 'Download patch',
    qcTitle: 'Quality control',
    issue: 'Issue',
    issueEmpty: 'Empty',
    issuePlaceholder: 'Placeholder mismatch',
    issueUntranslated: 'Untranslated',
    noIssues: 'No issues found',
    exportTitle: 'Import / export',
    exportNote: 'Export the client i18n catalog to a downloadable format. Function values (parametrized) are excluded.',
    exportCount: (n: number) => `${n.toLocaleString()} keys`,
    importNote: 'Import (writing translations back) needs a write endpoint — planned (DEPS lane A).',
    packsTitle: 'Language packs',
    pluralsTitle: 'Pluralization rules',
    pluralsNote: 'CLDR plural categories per language — a reference for translators.',
    title: 'Languages',
    note: 'Client i18n catalogs (uz/ru/en). Keys are enforced by the TS type.',
    language: 'Language',
    keys: 'Keys',
    untranslated: 'Untranslated (~)',
    coverage: 'Coverage',
    heuristic:
      '“Untranslated” = value identical to English (heuristic) — proper nouns (MIME, General) may be false positives.',
    keysTitle: 'Keys list',
    missingTitle: 'Missing translations',
    statsTitle: 'Translation stats',
    searchPh: 'Search by key or value…',
    value: 'value',
    namespace: 'Namespace',
    allTranslated: 'All translated',
    showingCap: (n: number, total: number) => `Showing ${n.toLocaleString()} / ${total.toLocaleString()}`,
  },

  legal: {
    queue: 'Legal requests',
    queueNote: 'Log incoming legal/GDPR requests and track fulfillment.',
    log: 'Log request',
    created: 'Request logged',
    createFailed: 'Not saved',
    kind: 'Kind',
    authority: 'Requesting authority',
    reference: 'Reference / case #',
    jurisdiction: 'Jurisdiction',
    affected: 'Affected user',
    details: 'Details',
    fulfilled: 'Fulfilled',
    rejected: 'Rejected',
    card: 'Request card',
    updateStatus: 'Update status',
    resolutionPlaceholder: 'Fulfillment note (optional)',
    statusChanged: 'Status changed',
    data: 'Data',
    preservation: 'Preservation order',
    removal: 'Content removal',
    blocking: 'Blocking',
    emergency: 'Emergency',
    rejectedTitle: 'Rejected requests',
    transparencyTitle: 'Transparency report',
    total: 'Total',
    byKind: 'By type',
    byAuthority: 'By authority',
    byJurisdiction: 'By jurisdiction',
  },

  verif: {
    queue: 'Verification requests',
    queueNote: 'Users request a verified badge. On approval an official badge is granted.',
    kind: 'Kind',
    kindIdentity: 'Identity',
    kindOrg: 'Organization',
    kindNotable: 'Notable',
    evidence: 'Evidence',
    approved: 'Approved',
    rejected: 'Rejected',
    registry: 'Verified registry',
    card: 'Request card',
    approve: 'Approve',
    reject: 'Reject',
    approveHint: 'On approval: the user gets an official badge (is_official).',
    approvedToast: 'Request approved, official badge granted',
    rejectedToast: 'Request rejected',
    decideFailed: 'Could not save decision',
    rejectedTitle: 'Rejected requests',
    statsTitle: 'Verification stats',
    total: 'Total',
    byKind: 'By type',
    revoke: 'Revoke badge',
    revokeTitle: (name: string) => `Revoke official badge — ${name}`,
    revokePrompt: 'The user will lose the official badge (is_official=false). The action is written to the audit log.',
    revokedToast: 'Official badge revoked',
  },

  appeals: {
    queue: 'Appeals',
    queueNote: 'Users contest a ban/strike decision.',
    kind: 'Kind',
    kindBan: 'Ban',
    kindStrike: 'Strike',
    granted: 'Granted',
    rejected: 'Rejected',
    card: 'Appeal card',
    decisionNote: 'Decision note',
    decision: 'Decision',
    notePlaceholder: 'Decision note (optional)',
    reject: 'Reject',
    grant: 'Grant',
    grantHint: 'On grant: ban → lifted, strike → expired.',
    grantedToast: 'Appeal granted, enforcement reversed',
    rejectedToast: 'Appeal rejected',
    decideFailed: 'Could not save decision',
  },

  strikes: {
    title: 'Strike history',
    queue: 'Recent strikes',
    queueNote: 'Progressive discipline. Strikes come from a report decision or are issued manually.',
    severity: 'Severity',
    issuedBy: 'Issued by',
    active: 'Active',
    expired: 'Expired',
    activePoints: 'Active points',
    issue: 'Issue strike',
    issueBtn: 'Issue',
    issued: 'Strike issued',
    issueFailed: 'Could not issue strike',
    none: 'No strikes',
    statsTitle: 'Restriction stats',
    total: 'Total',
    usersStruck: 'Users struck',
  },


  mod: {
    queue: 'Moderation queue',
    queueNote: 'Reports come from users. Filter by category/status.',
    category: 'Category',
    priorityTitle: 'Priority queue',
    target: 'Target',
    reporter: 'Reporter',
    reason: 'Reason',
    pending: 'Pending',
    resolved: 'Resolved',
    dismissed: 'Dismissed',
    reportCard: 'Report card',
    resolution: 'Resolution note',
    decision: 'Decision',
    resolutionPlaceholder: 'Action taken (optional)',
    dismiss: 'Dismiss (no violation)',
    resolve: 'Resolve (action taken)',
    resolvedToast: 'Report resolved',
    dismissedToast: 'Report dismissed',
    decideFailed: 'Could not save decision',
    banUser: 'Ban + resolve',
    bannedToast: 'User banned, report closed',
    groups: 'Grouping (by target)',
    groupsNote: 'How many reports per target — most-reported on top.',
    reportCount: 'Reports',
    latest: 'Latest',
    reporterReliability: 'Reporter reliability',
    reporterReliabilityNote: 'High dismiss rate = many baseless reports (unreliable source).',
    dismissRate: 'Dismiss rate',
    catCsam: 'CSAM',
    catViolence: 'Violence',
    catFraud: 'Fraud',
    catSpam: 'Spam',
    catCopyright: 'Copyright',
    catDoxxing: 'Doxxing',
    catOther: 'Other',
  },

  flags: {
    title: 'Feature flags',
    card: 'Flag card',
    key: 'Key',
    value: 'Value',
    on: 'Enabled',
    off: 'Disabled',
    changelog: 'Flag change log',
    type: 'Type',
    remoteTitle: 'Remote config values',
    toggleTitle: 'Enable / disable flags',
    toggleFailed: 'Failed to change flag',
    killTitle: 'Kill-switch (emergency off)',
    killNote: 'Instantly disable active flags. Every disable is written to the audit log.',
    killEmpty: 'No active flags — nothing to disable.',
    kill: 'Disable',
    killed: 'Flag disabled',
    killConfirmTitle: 'Emergency-disable flag',
    killConfirmDesc: (key: string) => `“${key}” will be disabled immediately for ALL users. Type the key to confirm.`,
    rolloutTitle: 'Gradual rollout (%)',
    rolloutNote: 'Each flag is enabled for N% of users (stable bucketing — the enabled set only grows as the percentage rises).',
    rolloutSaved: (pct: number) => `Rollout set to ${pct}%`,
    historyTitle: 'Flag change history',
    historyEmpty: 'No history for this flag (never changed yet).',
    pickFlag: 'Pick a flag',
    current: 'current',
    restore: 'Restore',
    restored: 'Value restored',
    restoreTitle: 'Restore previous value',
    restoreDesc: (key: string, value: string) => `Flag “${key}” will be reverted to “${value}”.`,
    segTitle: 'Flag audience segment',
    segPlatforms: 'Platforms',
    segCountries: 'Countries',
    segCountrySearch: 'Search country (code or name)…',
    segNoneNote: 'No restriction — the flag is visible everywhere (where enabled).',
    segActiveNote: 'The flag will be enabled ONLY in the selected countries/platforms.',
    segSaved: 'Segment saved',
    segCleared: 'Segment cleared (no restriction)',
    segClear: 'Remove restriction',
    depsTitle: 'Flag dependencies',
    depsPrereqs: 'Prerequisite flags',
    depsNoneNote: 'No dependencies — the flag is independent.',
    depsActiveNote: (key: string) => `“${key}” turns on ONLY when the following flags are on.`,
    depsSaved: 'Dependencies saved',
    depsCleared: 'Dependencies cleared',
    depsClear: 'Clear dependencies',
    schemaTitle: 'Flag value schema',
    schemaType: 'Type',
    schemaValues: 'Allowed values',
    schemaValuesHint: 'Comma-separated (e.g. light, dark, auto).',
    schemaNone: 'No schema',
    schemaValid: 'Valid',
    schemaInvalid: 'Invalid',
    schemaSaved: 'Schema saved',
    schemaCleared: 'Schema removed',
    schemaClear: 'Remove schema',
  },

  exp: {
    title: 'A/B experiments',
    create: 'New experiment',
    empty: 'No experiments yet.',
    on: 'Active',
    off: 'Off',
    name: 'Name',
    nameHint: 'Only [a-z0-9_], 1–64 chars. Immutable after creation.',
    nameInvalid: 'Invalid name ([a-z0-9_])',
    enabled: 'Active (a variant is served to clients)',
    variants: 'Variants (key + weight)',
    addVariant: 'Add variant',
    needTwo: 'At least 2 variants required',
    saved: 'Experiment saved',
    saveFailed: 'Could not save',
    deleted: 'Experiment deleted',
    deleteTitle: 'Delete experiment',
    deleteDesc: (name: string) => `Experiment “${name}” will be deleted. Users stop receiving a variant.`,
    distribution: (n: number) => `Distribution (${n.toLocaleString()} sample users — real assignment)`,
  },

  auditSearch: {
    title: 'Audit search',
    anyAction: 'Any action',
    since: 'From',
    until: 'To',
    search: 'Search',
    hint: 'Fill in the filters and press “Search”.',
    results: (n: number) => `${n.toLocaleString()} records`,
  },

  suspStaff: {
    tab: 'Suspicious',
    note: 'Staff by destructive-action volume (ban/delete/block/…); a statistical outlier is “suspicious”.',
    flaggedCount: (n: number) => (n > 0 ? `${n} suspicious` : 'none suspicious'),
    days: (n: number) => `${n}d`,
    destructive: 'Destructive',
    total: 'Total',
    targets: 'Targets',
    reasonHigh: 'High volume',
    reasonOutlier: 'Anomaly',
  },

  metrics: {
    activeUsers: 'DAU / WAU / MAU',
    wau: 'WAU (7d)',
    wauNote: 'WAU (weekly active) is not yet exposed by the analytics endpoint — follow-up.',
    newUsers: 'New users',
    botEcosystem: 'Bot ecosystem stats',
    botsTotal: 'Total bots',
    botsOfficial: 'Official bots',
    botsActive: 'Active bots',
    botsRevoked: 'Revoked',
    botsMiniApp: 'With Mini App',
    botsBusiness: 'With Business',
    botsInline: 'With inline',
    botsGateway: 'In gateway registry',
    botApps: 'Mini Apps',
    botGames: 'Games',
    botActiveUsers: 'Bot active users',
    messageVolume: 'Message volume',
    funnel: 'Funnel analysis',
    totalUsers: 'Total users',
    sentFirst: 'Sent first message',
  },

  analytics: {
    byPlatform: 'By platform',
    byChatType: 'By chat type',
    byVersion: 'By version',
    byAppVersion: 'By app version',
    byOsVersion: 'By OS version',
    byLanguage: 'By language',
    pointsEconomy: 'Ball economy',
    pointsNote: 'Aggregate over the internal Ball system (cash-free, activity-based).',
    pointsCirculating: 'Ball circulating',
    pointsEarned: 'Lifetime earned',
    pointsSpent: 'Total spent',
    pointsHolders: 'Holders',
    pointsSuspended: 'Suspended',
    pointsPurchases: 'Purchases',
    pointsEarnByReason: 'Earn by source',
    pointsSpendByItem: 'Spend by item',
    moderationKpi: 'Moderation KPI',
    modPending: 'Pending',
    modResolved: 'Resolved',
    modDismissed: 'Dismissed',
    modTotal: 'Total reports',
    modCreated7d: 'New (7d)',
    modResolved7d: 'Resolved (7d)',
    modAvgResolution: 'Avg resolution time',
    modByCategory: 'By category',
    modTrend: 'New reports',
    daysLabel: (d: number) => `${d}d`,
    channelBoosts: 'Channel boosts',
    channelBoostsNote: 'Channel support (boosts) — Ball-based, cash-free.',
    cbTotal: 'Total boosts',
    cbChannels: 'Boosted channels',
    cbBoosters: 'Boosters',
    cb7d: 'Boosts (7d)',
    cbTop: 'Top channels',
    cbTrend: 'Boosts trend',
    topBots: 'Top bots',
    activity: 'Activity (by hour)',
    activityNote: 'Last 30 days — message activity by weekday × hour.',
    weekdays: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
    sessionDuration: 'Session duration',
    sessionNote: 'Device session lifespan: from creation to last activity (or revoke time).',
    totalSessions: 'Total sessions',
    activeSessions: 'Active sessions',
    avgSession: 'Average',
    medianSession: 'Median',
    durationDist: 'Duration distribution',
    durLt1h: '<1 h',
    dur1h1d: '1h–1d',
    dur1d7d: '1–7 d',
    dur7d30d: '7–30 d',
    durGt30d: '>30 d',
    durUnits: { d: 'd', h: 'h', m: 'm' },
    retention: 'Retention',
    retentionNote: 'Rolling D1/D7/D30: share of users who registered N days ago and were still active N+ days later.',
    churn: 'Churn',
    churnNote: 'Rolling D1/D7/D30: share of the cohort that did not return (1 − retention).',
    byWindow: 'By window',
    channelGrowth: 'Channel growth',
    channelGrowthNote: 'Channels and member joins (last 30 days, daily series).',
    totalChannels: 'Total channels',
    totalChannelMembers: 'Total members',
    newChannels7d: 'New channels (7d)',
    newJoins7d: 'New joins (7d)',
    newChannelsSeries: 'New channels (daily)',
    memberJoinsSeries: 'Member joins (daily)',
    supportKpi: 'Support KPI',
    supportKpiNote: 'Ticket status, resolution time and breakdowns (support_tickets, pure aggregate).',
    ticketsTotal: 'Total tickets',
    ticketsOpen: 'Open',
    ticketsPending: 'Pending',
    ticketsClosed: 'Closed',
    ticketsNew7d: 'New (7d)',
    ticketsClosed7d: 'Closed (7d)',
    ticketAvgResolution: 'Avg resolution',
    ticketMedianResolution: 'Median resolution',
    ticketByCategory: 'By category',
    ticketByPriority: 'By priority',
    newTicketsSeries: 'New tickets (daily)',
    adRevenue: 'Ad revenue',
    adRevenueNote: 'Pure aggregate over ad campaigns: revenue (spent), budget, impressions/clicks, breakdown by status/placement.',
    adCampaigns: 'Campaigns',
    adActive: 'Active',
    adImpressions: 'Impressions',
    adClicks: 'Clicks',
    adRevenueTotal: 'Revenue (spent)',
    adBudget: 'Budget',
    adCtr: 'CTR',
    adByStatus: 'By status',
    adByPlacement: 'By placement (revenue)',
    inviteLinks: 'Invite links',
    inviteLinksNote: 'Pure aggregate over invite links: link/usage counts, active/revoked, breakdown by chat type, new-links series over 30 days.',
    linksTotal: 'Total links',
    linksActive: 'Active',
    linksRevoked: 'Revoked',
    linkUses: 'Total uses',
    linksNew7d: 'New (7d)',
    linkAvgUses: 'Avg uses',
    linkUsesByType: 'By chat type (uses)',
    newLinksSeries: 'New links (daily)',
    revenueDash: 'Revenue dashboard',
    revenueDashNote: 'Consolidated platform revenue: ads (spent), marketplace fees (completed trades) and bot Stars. PII-free.',
    revTotal: 'Total revenue',
    revSourceAd: 'Ads',
    revSourceMarket: 'Marketplace fees',
    revSourceStar: 'Bot Stars',
    revMarketGmv: 'Marketplace GMV',
    revMarketTrades: 'Trades',
    revStarCharges: 'Star charges',
    revBySource: 'By source',
    revPerDay: 'Revenue (daily: market+Stars)',
    metricsDict: 'Metrics dictionary',
    metricsDictNote: 'Single source of truth for analytics metrics: each metric, its source table and computation formula (technical definition).',
    mdMetric: 'Metric',
    mdSource: 'Source (table)',
    mdFormula: 'Formula',
    arpuTitle: 'ARPU / ARPPU',
    arpuNote: 'Average revenue per user (ARPU) and per paying user (ARPPU). Revenue = ads + marketplace fees + Stars; payer = Stars payer ∪ marketplace buyer. PII-free.',
    arpuPayers: 'Paying users',
    arpuValue: 'ARPU',
    arppuValue: 'ARPPU',
    arpuPayingRatio: 'Paying ratio',
    marketTitle: 'Marketplace analytics',
    marketNote: 'Pure aggregate over marketplace listings, trades, sellers, bids and reviews. GMV = sum of trades. PII-free.',
    marketDetailLink: 'Marketplace analytics (detail) →',
    mktListings: 'Listings',
    mktActive: 'Active',
    mktSold: 'Sold',
    mktAuctions: 'Auctions',
    mktSellers: 'Sellers',
    mktVerified: 'Verified',
    mktTrades: 'Trades',
    mktGmv: 'GMV',
    mktBids: 'Bids',
    mktReviews: 'Reviews',
    mktAvgRating: 'Avg rating',
    mktByStatus: 'By status',
    mktNewListings: 'New listings (daily)',
    liveTitle: 'Live analytics',
    liveNote: 'Pure aggregate over live streams, gifts, viewers, comments and battles. Gift amount = sum of live_gifts. PII-free.',
    liveDetailLink: 'Live analytics →',
    liveStreams: 'Streams',
    liveLive: 'Live now',
    liveHosts: 'Hosts',
    liveLikes: 'Likes',
    liveGifts: 'Gifts',
    liveGiftAmount: 'Gift amount',
    liveViewers: 'Viewers',
    liveComments: 'Comments',
    liveBattles: 'Battles',
    liveAvgDuration: 'Avg duration',
    liveByStatus: 'By status',
    liveGiftsSeries: 'Gifts (daily amount)',
    storiesTitle: 'Stories analytics',
    storiesNote: 'Pure aggregate over stories and views: story count/active, authors, views, viewers, 30-day series. PII-free.',
    storiesDetailLink: 'Stories analytics →',
    storiesTotal: 'Stories',
    storiesActive: 'Active',
    storiesAuthors: 'Authors',
    storyViews: 'Views',
    storyViewers: 'Viewers',
    storyAvgViews: 'Avg views',
    storiesSeries: 'New stories (daily)',
    storyViewsSeries: 'Views (daily)',
    walletTitle: 'Wallet/Token economy',
    walletNote: 'Pure aggregate over the Take wallet (wallets) and user currencies (currencies/token). In circulation = sum of wallet balances. Separate from Points. PII-free.',
    walletDetailLink: 'Wallet/Token economy →',
    walBalance: 'In circulation (Take)',
    walWallets: 'Wallets',
    walTxns: 'Transactions',
    walTransferVol: 'Transfer volume',
    walCurrencies: 'Currencies',
    walSupply: 'Total supply',
    walHolders: 'Token holders',
    walTokenTxns: 'Token txns',
    walByKind: 'By kind',
    walTxnsSeries: 'Transactions (daily)',
    pollsTitle: 'Polls analytics',
    pollsNote: 'Pure aggregate over polls, quizzes and votes: poll count/open, quiz/anonymous/multiple, votes, voters, 30-day series. PII-free.',
    pollsDetailLink: 'Polls analytics →',
    pollsTotal: 'Polls',
    pollsOpen: 'Open',
    pollVotes: 'Votes',
    pollVoters: 'Voters',
    pollQuizzes: 'Quizzes',
    pollAnon: 'Anonymous',
    pollMulti: 'Multiple choice',
    pollOptions: 'Options',
    pollAvgVotes: 'Avg votes',
    pollsSeries: 'New polls (daily)',
    pollVotesSeries: 'Votes (daily)',
    reactTitle: 'Reactions analytics',
    reactNote: 'Pure aggregate over message reactions: reaction count, unique emojis, reactors, reacted messages, top emojis, 30-day series. PII-free.',
    reactDetailLink: 'Reactions analytics →',
    reactTotal: 'Reactions',
    reactEmojis: 'Unique emojis',
    reactReactors: 'Reactors',
    reactMessages: 'Reacted messages',
    reactTopEmojis: 'Top emojis',
    reactSeries: 'Reactions (daily)',
    mediaTitle: 'Media/storage analytics',
    mediaNote: 'Pure aggregate over files and variants: file count/ready, owners, storage (bytes), variants, breakdown by kind, 30-day series. PII-free.',
    mediaDetailLink: 'Media/storage analytics →',
    mediaFiles: 'Files',
    mediaReady: 'Ready',
    mediaOwners: 'Owners',
    mediaVariants: 'Variants',
    mediaStorage: 'Storage',
    mediaVariantStorage: 'Variant storage',
    mediaByKind: 'By kind',
    mediaSeries: 'New files (daily)',
    contactsTitle: 'Contacts growth',
    contactsNote: 'Pure aggregate over imported contacts: contact count, owners, matched in app, match rate, 30-day series. PII-free.',
    contactsDetailLink: 'Contacts growth →',
    contactsTotal: 'Contacts',
    contactsOwners: 'Owners',
    contactsMatched: 'In app',
    contactsMatchRate: 'Match rate',
    contactsSeries: 'New contacts (daily)',
    // A16.16/20/21/22 — aggregate analytics (server-side, pure SQL)
    funnelCompareTitle: 'Funnel comparison',
    funnelCompareNote: 'How a signup cohort moves through the funnel: current 7 days vs previous 7 days.',
    thisWeek: 'Current 7 days',
    lastWeek: 'Previous 7 days',
    stepRegistered: 'Registered',
    stepVerified: 'Verified',
    stepActive: 'Activated',
    stepRetained: 'Retained (7d)',
    segmentCompareTitle: 'Segment comparison',
    segmentCompareNote: 'Top 6 countries: users, 7-day active, messages.',
    metricUsers: 'Users',
    metricActive7d: 'Active (7d)',
    metricMessages: 'Messages',
    cohortTitle: 'Cohort analysis',
    cohortNote: 'Weekly signup cohort × week-offset retention (% active).',
    cohortCol: 'Cohort (week)',
    cohortSize: 'Size',
    weekLabel: (n: number) => `Week ${n}`,
    forecastTitle: 'Forecast',
    forecastNote: 'Daily registrations: 21 days of actuals + 7-day moving-average (MA) projection.',
    actual: 'Actual',
    predicted: 'Forecast (MA-7)',
    regsPerDay: 'Registrations (daily)',
  },

  skeleton: {
    bannerTitle: 'Planned screen (skeleton)',
    needs: 'Requires',
    adapterReady: 'UI layout scaffold + data adapter are ready — goes live when the pipeline is connected.',
    needsEvents: 'Event telemetry + ClickHouse (event-ETL)',
    eventDiscovery: 'Event discovery',
    eventTaxonomy: 'Event taxonomy',
    userPath: 'User path',
    funnelCompare: 'Funnel comparison',
    segmentCompare: 'Segment comparison',
    cohortBuilder: 'Cohort builder',
    forecasting: 'Forecasting',
    serviceQuality: 'Service quality (SLO/SLI)',
    reportBuilder: 'Report builder',
    needsObservability: 'Observability pipeline (Prometheus/Grafana + SLO targets)',
    needsReportEngine: 'Report-builder engine (query builder + export)',
    alertsCenter: 'Alerts center',
    dashboardBuilder: 'Dashboard builder',
    needsAlerting: 'Alerting infrastructure (alert rules + channels)',
    needsDashboardEngine: 'Widget-builder engine (drag-and-drop + persistence)',
    needsPayments: 'Payment provider + AML infra (GROM is cash-free)',
    needsRevenue: 'Revenue ETL + monetization data',
  },

  sms: {
    title: 'SMS providers',
    note: 'Infobip provider status, real balance and sent-SMS statistics (country/status/recent). OTP codes are masked.',
    balance: 'Balance',
    totalSent: 'Total sent',
    mode: 'Mode',
    notConfigured: 'Not configured',
    byCountry: 'By country',
    byStatus: 'By status',
    recent: 'Recent messages',
    colNumber: 'Number',
    colMessage: 'Message',
    colStatus: 'Status',
    colTime: 'Time',
  },

  music: {
    title: 'Music library',
    note: 'Moderate tracks (approve/reject), mark premium, delete.',
    stAll: 'All',
    stPending: 'Pending',
    stApproved: 'Approved',
    stRejected: 'Rejected',
    colTitle: 'Title',
    colArtist: 'Artist',
    colGenre: 'Genre',
    colDuration: 'Duration',
    colPlays: 'Plays',
    colStatus: 'Status',
    approve: 'Approve',
    reject: 'Reject',
    premium: 'Premium',
    makePremium: 'Make premium',
    unpremium: 'Remove premium',
    confirmDelete: 'Confirm?',
  },

  community: {
    title: 'Communities',
    note: 'User-created sets of linked channels/groups (owner + group count).',
    colName: 'Name',
    colOwner: 'Owner',
    colGroups: 'Groups',
    colCreated: 'Created',
  },

  users: {
    ban: 'Ban',
    unban: 'Unban',
    banLog: 'Ban history',
    fairnessTitle: 'Fairness report',
    fairnessNote: 'Ban-to-unban ratio — a moderation-fairness indicator (GET /admin/audit).',
    unbanRatio: 'Unban ratio',
    activeRestrictions: 'Active restrictions',
    bannedAt: 'Banned',
    viewCard: 'Card',
    cardTitle: 'User card',
    userNumber: 'ID number',
    email: 'Email',
    birthday: 'Birthday',
    bio: 'Bio',
    lastSeen: 'Last seen',
    bannedReason: 'Ban reason',
    sessions: 'Sessions',
    sessionsTitle: 'Sessions and devices',
    platform: 'Platform',
    device: 'Device',
    osVersion: 'OS',
    appVersion: 'App',
    revoked: 'Revoked',
    banTitle: (name: string) => `Ban ${name}`,
    banDesc: 'The user will be banned: unable to sign in or send messages. Reason is required.',
    freeze: 'Freeze',
    unfreeze: 'Unfreeze',
    freezeTitle: (name: string) => `Freeze ${name}`,
    freezeDesc: 'Temporary restriction pending review: the user will be unable to send messages. Reason is required.',
    freezeReason: 'Freeze reason',
    verify: 'Grant verification',
    unverify: 'Remove verification',
    forceLogout: 'Force logout',
    forceLogoutTitle: (name: string) => `Log ${name} out of all sessions`,
    forceLogoutDesc:
      'ALL of the user’s sessions will be terminated (not banned — they can sign in again). For a suspected device/token compromise. Reason is written to the audit log.',
    forceLogoutPending: 'This action will work once the backend is deployed (patch pending).',
    actionDone: 'Done',
    actionFailed: 'Action failed',
    groups: 'Groups/channels',
    wallet: 'Wallet',
    ownedGroups: 'Owned groups/channels',
    contact: 'Contact details',
    bulkBan: 'Ban all',
    bulkLogout: 'Log out all',
    bulkAllMatchingWarn:
      'Bulk actions apply only to rows selected BY HAND — the server has no bulk endpoint. Select rows first.',
    bulkResult: (ok: number, fail: number) =>
      fail ? `${ok} succeeded, ${fail} failed` : `${ok} succeeded`,
    gdprExport: 'GDPR export',
    gdprExportTitle: (name: string) => `${name} — GDPR data export`,
    gdprExportDesc:
      'Opens a GDPR "export" request for this user (30-day deadline). The request lands in the /gdpr-requests queue and is audited.',
    gdprExportDone: 'GDPR request created',
    label: 'Label',
    labelTitle: (name: string) => `${name} — moderation label`,
    labelDesc: 'A label does not block the account/chat: its About is replaced with the label text and can no longer be edited. For a channel/bot the owner is also frozen.',
    labelClear: 'Clear label',
    labelScam: 'Scam',
    labelFake: 'Fake',
    labelFraud: 'Fraud',
    labelSpam: 'Spam',
    deleteAccount: 'Delete account',
    deleteAccountTitle: (name: string) => `Permanently delete ${name}`,
    deleteAccountDesc:
      'PERMANENT, cannot be undone: photo/bio/name are wiped, the username is freed for anyone else to claim, the account disappears from search, and every session is logged out. This is a real account.deleteAccount, not a ban/freeze.',
  },

  reports: {
    title: 'Reports',
    note: 'Real reports submitted via account.reportPeer/messages.report/etc. — those RPCs used to persist nothing.',
    reporter: 'Reporter',
    target: 'Target',
    reason: 'Reason',
    comment: 'Comment',
    status: 'Status',
    statusOpen: 'Open',
    statusResolved: 'Resolved',
    statusDismissed: 'Dismissed',
    resolve: 'Resolve',
    dismiss: 'Dismiss',
    resolveTitle: 'Resolve report',
    dismissTitle: 'Dismiss report',
    resolutionLabel: 'Comment (optional)',
    resolvedBy: 'Resolved by',
    createdAt: 'Submitted',
    emptyTitle: 'No reports',
    deleteMessage: 'Delete message',
    deleteMessageTitle: 'Delete message',
    deleteMessageNoticeLabel: 'Notice text shown instead of the message',
    deleteMessageDefaultHint: 'Leave blank for "Message deleted for violating Grom Messenger policy"',
    deleteMessageNoTarget: 'This report has no message attached',
  },

  verification: {
    approve: 'Approve',
    reject: 'Reject',
    approveTitle: 'Approve verification request',
    rejectTitle: 'Reject verification request',
    statusApproved: 'Approved',
    statusRejected: 'Rejected',
    emptyTitle: 'No verification requests',
    platformProofs: 'Social media proof links',
    pressProofs: 'Press article links',
    noProofLinks: 'No links collected yet (request predates this feature)',
  },

  support: {
    title: 'Support',
    note: 'Real tickets from conversations with the "Grom Support" bot — it used to be a plain account with no logic at all. Ban/freeze appeals live in the separate "Appeals" section.',
    ticket: 'Ticket',
    user: 'User',
    status: 'Status',
    statusOpen: 'Open',
    statusClosed: 'Closed',
    createdAt: 'Opened',
    closedAt: 'Closed',
    closedBy: 'Closed by',
    messages: 'Messages',
    replyPlaceholder: 'Reply to the user…',
    reply: 'Reply',
    close: 'Close ticket',
    emptyTitle: 'No tickets',
    appealBadge: 'Appeal',
    approveAppeal: 'Approve',
    rejectAppeal: 'Reject',
    resolutionPlaceholder: 'Comment for the user (optional)…',
    appealKind: 'Type',
    appealKindFreeze: 'Account freeze',
    appealKindBan: 'Account ban',
    appealKindChannel: 'Channel/chat ban',
    appealsEmptyTitle: 'No appeals',
  },

  system: {
    title: 'System settings',
    desc: 'Platform config the app actually reads (app_settings)',
    save: 'Save',
    saved: 'Settings saved',
    saveError: 'Save failed',
    loadError: 'Could not load settings',
    maintenance: 'Maintenance mode',
    maintenanceDesc: 'Returns 503 on user requests; admin, login and internal services stay up.',
    maintenanceWarn: 'The app is currently DOWN for users.',
    liveSection: 'Live stream',
    streamerPct: 'Streamer gift share (%)',
    maxCohosts: 'Max co-hosts',
    confirmMaintTitle: 'Enable maintenance mode?',
    confirmMaint: 'All user-facing requests will return 503 until you turn this off. Admins keep access.',
    confirmMaintOk: 'Enable',
  },
  market: {
    title: 'Market',
    sellerApps: 'Seller applications',
    categories: 'Categories',
    shop: 'Shop',
    region: 'Region',
    activity: 'Activity',
    status: 'Status',
    pending: 'Pending',
    approved: 'Approved',
    rejected: 'Rejected',
    suspended: 'Suspended',
    none: 'None',
    approve: 'Approve',
    reject: 'Reject',
    verify: 'Mark trusted',
    unverify: 'Remove badge',
    rejectReason: 'Reject reason',
    rejectPrompt: 'Enter the rejection reason',
    rating: 'Rating',
    deals: 'Deals',
    verified: 'Trusted',
    created: 'Date',
    done: 'Done',
    failed: 'Action failed',
    newCategory: 'New category',
    editCategory: 'Edit',
    addCategory: 'Add category',
    catTitle: 'Title',
    catIcon: 'Icon (name)',
    catPosition: 'Order',
    catEnabled: 'Enabled',
    catListings: 'Listings',
    catDisable: 'Hide',
    save: 'Save',
    cancel: 'Cancel',
  },

  points: {
    title: 'Ball',
    earnRules: 'Earn rules',
    catalog: 'Catalog',
    suspicions: 'Suspicions',
    event: 'Event',
    amount: 'Amount',
    dailyCap: 'Daily cap',
    enabled: 'Enabled',
    unlimited: 'Unlimited',
    edit: 'Edit',
    save: 'Save',
    cancel: 'Cancel',
    done: 'Done',
    failed: 'Action failed',
    code: 'Code',
    itemTitle: 'Title',
    price: 'Price',
    category: 'Category',
    catCosmetic: 'Cosmetic',
    catBoost: 'Boost',
    catGift: 'Gifts',
    purchases: 'Purchases',
    newItem: 'New item',
    addItem: 'Add item',
    editItem: 'Edit',
    disable: 'Hide',
    reason: 'Reason',
    score: 'Score',
    clear: 'Clear',
    sanction: 'Freeze',
    statusOpen: 'Open',
    statusCleared: 'Cleared',
    statusSanctioned: 'Frozen',
    user: 'User',
  },

  stickers: {
    moderation: 'Sticker sets',
    empty: 'No sticker sets',
    title: 'Set',
    status: 'Status',
    count: 'Stickers',
    installs: 'Installs',
    reason: 'Reason',
    approve: 'Approve',
    reject: 'Reject',
    rejectPrompt: 'Enter a rejection reason. The set will be hidden from the store.',
    active: 'Active',
    rejected: 'Rejected',
    pending: 'Pending',
    done: 'Done',
    failed: 'Failed',
    economyHint: 'Economy',
  },

  finance: {
    amount: 'Amount',
    note: 'Note',
    peer: 'Counterparty',
    idempotency: 'Idempotency key',
    wallets: 'Wallets',
    balance: 'Balance',
    updated: 'Updated',
    dashboard: 'Finance dashboard',
    dashboardNote: 'Over the window the server returned (top-N wallets + recent transactions).',
    topWalletsSum: 'Top wallets sum',
    walletsShown: 'Wallets shown',
    recentTxns: 'Recent transactions',
  },

  bots: {
    official: 'Official',
    emptyTitle: 'No bots',
    emptyDesc: 'No bots created yet. They will appear once the bot platform adds them.',
    card: 'Bot card',
    gatewayNote:
      'Owner, stats, webhook and token history appear once the bot-platform gateway is connected (A5.03–A5.09).',
    statusLabel: 'Status',
    active: 'Active',
    disabled: 'Disabled',
    descLabel: 'Description',
    aboutLabel: 'About',
    commandsLabel: 'Commands',
    noCommands: 'No commands',
    verifierGrant: 'Grant verifier status',
    verifierRevoke: 'Revoke verifier status',
    verifierGrantTitle: 'Verifier status',
    verifierGrantDesc: 'The bot will be able to place a "verified by X" badge on other accounts (bots.setCustomVerification).',
    verifierIcon: 'Icon id (document_id)',
    verifierCompany: 'Company name',
    verifierDescription: 'Description (optional)',
    superadminOnly: 'Editing a bot is superadmin-only.',
    edit: 'Edit',
    addCommand: 'Add command',
    cmdName: 'Command',
    ownerTitle: 'Owner',
    noOwner: 'No owner assigned',
    webhookTitle: 'Webhook',
    noWebhook: 'No webhook set',
    whPending: 'Pending',
    whMaxConn: 'Max connections',
    whLastError: 'Last error',
    appsTitle: 'Mini Apps',
    noApps: 'No Mini Apps',
    businessTitle: 'Business connections',
    noBusiness: 'No business connections',
    affiliateTitle: 'Affiliate program',
    noAffiliate: 'No affiliate program',
    commission: 'Commission',
    duration: 'Duration (months)',
    gamesTitle: 'Games',
    noGames: 'No games',
    players: 'Players',
    reportsTitle: 'Reports',
    noReports: 'No reports',
    block: 'Block',
    blockTitle: 'Block bot',
    blockDesc: 'The bot account will be banned. This is a strong action.',
    statsTitle: 'Statistics',
    statMsgs: 'Messages',
    statUpdates: 'Updates',
    statToday: 'today',
    appSettingsTitle: 'Mini App theme',
    noAppSettings: 'Theme not configured',
    themeBg: 'Background color',
    themeHeader: 'Header color',
    previewMediaTitle: 'Screenshots / preview',
    noPreviewMedia: 'No preview media',
    tokenTitle: 'API key',
    noToken: 'No token data',
    tokenActive: 'Active',
    tokenRevoked: 'Revoked',
    tokenIssued: 'Issued',
    tokenRotated: 'Rotated',
    tokenRevokedAt: 'Revoked',
    makeOfficial: 'Mark official',
    unmakeOfficial: 'Remove official',
    enable: 'Enable',
    disable: 'Disable',
    officialTitle: 'Mark bot as official',
    officialDesc: 'The bot gets the official badge (✓) and is shown as official.',
    unofficialTitle: 'Remove official badge',
    unofficialDesc: 'The official badge is removed from the bot.',
    enableTitle: 'Enable bot',
    enableDesc: 'The bot is re-activated and will accept requests.',
    disableTitle: 'Disable bot',
    disableDesc: 'The bot is disabled and stops accepting requests (reversible).',
    actionDone: 'Done',
    actionFailed: 'Action failed',
    // A5.11/13/19/25 — lifecycle + mini-app safety
    restrict: 'Restrict',
    restrictTitle: 'Restrict bot',
    restrictDesc: 'Bot capabilities (BotFather settings) will be changed. The action is written to the audit log.',
    capsJoinGroups: 'Can join groups',
    capsInline: 'Inline mode',
    capsBusiness: 'Business connections',
    deletedTag: 'Deleted',
    deleteBot: 'Delete bot',
    deleteBotTitle: 'Delete bot permanently',
    deleteBotDesc:
      'The bot will be soft-deleted (disappears from the platform) and its token revoked. This is a strong, irreversible action.',
    deleteApp: 'Delete app',
    deleteAppTitle: 'Delete mini app',
    deleteAppDesc: 'The mini app will be permanently removed from the bot platform. Irreversible.',
    urlCheck: 'URL check',
    urlChecking: 'Checking…',
    urlOk: 'URL looks safe',
    urlBad: 'Issues found in the URL',
  },

  gifts: {
    emptyTitle: 'No gifts',
    emptyDesc: 'No gifts in the catalog yet.',
    addGift: 'Add gift',
    addGiftTitle: 'Add a Star Gift',
    stars: 'Stars',
    convertStars: 'Convert to stars',
    upgradeStars: 'Upgrade price (stars)',
    upgradeStarsHint: '0 = this gift can never be upgraded to a collectible',
    limited: 'Limited edition',
    availabilityTotal: 'Total quantity',
    stickerSet: 'Sticker set',
    stickerSetPlaceholder: 'Pick a sticker set…',
    document: 'Animation / sticker',
    documentPlaceholder: 'Pick a document…',
    modelCount: 'Models',
    activeAuction: 'Active auction',
    launchAuction: 'Launch auction',
    launchAuctionTitle: 'Launch a Star Gift auction',
    launchAuctionDesc: 'Bidders pay in Stars (payments.sendStarsForm). Losing bids are refunded automatically once the auction ends.',
    minBidAmount: 'Minimum bid (stars)',
    giftsAvailable: 'Number of copies auctioned',
    durationHours: 'Duration (hours)',
    auctions: 'Auctions',
    auctionsEmptyTitle: 'No auctions',
    auctionsEmptyDesc: 'No auction has been launched yet.',
    status: 'Status',
    statusActive: 'Active',
    statusFinished: 'Finished',
    topBid: 'Top bid',
    endsAt: 'Ends',
    giftsLeft: 'Left',
    createSuccess: 'Gift created',
    launchSuccess: 'Auction launched',
    fillRequired: 'Fill in every required field',
  },

  gromauktion: {
    emptyTitle: 'No listings',
    emptyDesc: 'No GromAuktion listings yet.',
    username: 'Username',
    seller: 'Seller',
    theHouse: 'The house',
    sellerUserId: 'Seller user ID',
    sellerUserIdHint: '0 = the house (an admin-created listing for a username nobody currently owns)',
    price: 'Price (TAKE)',
    retroactive: 'Retroactive',
    buyer: 'Buyer',
    createdAt: 'Created',
    soldAt: 'Sold',
    statusActive: 'Active',
    statusSold: 'Sold',
    statusDelisted: 'Delisted',
    createListing: 'Create listing',
    createListingTitle: 'Create a GromAuktion listing',
    createListingDesc: 'The username must not already have an active listing.',
    createAuction: 'Create auction',
    createAuctionTitle: 'Create a GromAuktion auction',
    startingPrice: 'Starting price (TAKE)',
    minBidIncrement: 'Minimum bid increment (TAKE)',
    durationHours: 'Duration (hours)',
    auctionBadge: 'auction',
    endsAt: 'Ends',
    isNumber: 'This is a collectible number, not a username',
    numberValue: 'Number',
    createSuccess: 'Listing created',
    reprice: 'Reprice',
    repriceTitle: 'Change price',
    repriceSuccess: 'Price updated',
    delist: 'Delist',
    delistSuccess: 'Listing delisted',
    runMigration: 'Run retroactive migration',
    migrateCreated: 'Created',
    migrateSkipped: 'Already listed',
    migrateNothing: 'Nothing to migrate',
    fillRequired: 'Fill in every required field',
  },

  security: {
    staffActivity: 'Staff activity',
    actionCount: 'Action count',
    auditWindowNote: 'Aggregated over the last 200 audit records (not full history).',
  },

  invites: {
    title: 'Invite links',
    usage: 'Usage',
    creator: 'Creator',
    primary: 'Primary',
    expires: 'Expires',
  },

  geo: {
    sanctionsTitle: 'Sanctions lists',
    sanctionsNote: 'Countries on common OFAC/EU sanctions lists, cross-checked against the registry. "Review" = sanctioned but not blocked.',
    compliance: 'Compliance',
    compliant: 'Compliant',
    reviewNeeded: 'Review',
    block: 'Block',
    unblock: 'Unblock',
    blockTitle: (name: string) => `Block country “${name}”`,
    unblockTitle: (name: string) => `Unblock country “${name}”`,
    blockDesc: 'Users in this country will lose access. Level 4: a second admin must approve.',
    unblockDesc: 'This country will be reopened. Level 4: a second admin must approve.',
    impactUsers: 'registered users',
    reverseWindow: 'anytime',
    requestCreated: 'Four-eyes request created — awaiting approval in the A18.06 queue',
    requestFailed: 'Could not create request',
    changelog: 'Change log',
    countryCard: 'Country card',
    growth: 'Growth by country',
    newUsers: 'New users',
    totalUsers: 'Total users',
    dialPrefixes: 'Dial prefixes',
    dialPrefixesNote: 'Grouped by dial code; shared prefix (+7 = RU/KZ) is flagged.',
    shared: 'Shared',
  },

  fourEyes: {
    title: 'Four-eyes requests',
    subtitle: 'Level 4–5 actions awaiting a second approver',
    tab: 'Four-eyes',
    requester: 'Requested by',
    approver: 'Approved by',
    level: 'Level',
    expires: 'Expires',
    executeAfter: 'Executable from',
    requiredRole: 'Required role',
    impact: 'Impact (frozen at request time)',
    approve: 'Approve',
    reject: 'Reject',
    cancel: 'Stop',
    execute: 'Execute',
    actionDone: 'Done',
    actionFailed: 'Action failed',
    cancelPrompt: 'Reason for stopping (at least 10 characters):',
    reasonShort: 'Reason must be at least 10 characters',
    myRequests: 'My requests',
    openOnly: 'Open only',
    selfApprovalBlocked: 'You cannot approve your own request',
    executorIsRequester: 'Only the requester can execute the action',
    waitUntil: (t: string) => `Wait period ends at ${t}`,
    noEndpointTitle: 'Queue cannot be read from the server',
    noEndpointDesc:
      'The schema is ready and verified against the database (migration 000076), but no HTTP method reads it yet. The queue is not filled with fake data.',
  },

  staff: {
    title: 'Staff',
    subtitle: 'Roles, permissions and teams',
    member: 'Member',
    role: 'Role',
    roles: 'Roles',
    team: 'Team',
    status: 'Status',
    lastActive: 'Last active',
    createdAt: 'Added',
    addMember: 'Add member',
    permissionMatrix: 'Permission matrix',
    rolesNote: 'Roles from the client catalog (replaced once the server role catalog arrives). Creating/editing roles awaits a server endpoint.',
    allPerms: 'all (*)',
    jitAccess: 'Just-in-time access (JIT)',
    jitRemaining: (t: string) => `Just-in-time access — ${t} left`,
    offboard: 'Revoke all access',
    suspend: 'Suspend',
    emptyTitle: 'No staff members yet',
    emptyDesc: 'Add the first member or import existing ones.',
    superAdminWarning: (n: number) =>
      `${n} super admins. The spec allows no more than 2.`,
    inactive: 'Inactive',
    permCount: 'Permissions',
    lastLogin: 'Last login',
    roleSaved: 'Role updated',
    roleFailed: 'Could not change role',
  },

  /** Table column headers — never hardcoded in screens. */
  col: {
    id: 'ID',
    name: 'Name',
    username: '@username',
    phone: 'Phone',
    status: 'Status',
    registered: 'Registered',
    country: 'Country',
    code: 'Code',
    nationality: 'Nationality',
    dialCode: 'Dial code',
    registrations: 'Registrations',
    time: 'Time',
    staff: 'Staff',
    action: 'Action',
    target: 'Target',
    detail: 'Detail',
    type: 'Type',
    title: 'Title',
    ip: 'IP address',
  },

  status: {
    active: 'Active',
    pending: 'Pending',
    banned: 'Banned',
    blocked: 'Blocked',
    deleted: 'Deleted',
    review: 'In review',
    suspended: 'Suspended',
    open: 'Open',
    approved: 'Approved',
    rejected: 'Rejected',
    expired: 'Expired',
    executed: 'Executed',
    cancelled: 'Stopped',
  },

  time: {
    justNow: 'just now',
    secondsAgo: (n: number) => `${n} second${n === 1 ? '' : 's'} ago`,
    minutesAgo: (n: number) => `${n} minute${n === 1 ? '' : 's'} ago`,
    hoursAgo: (n: number) => `${n} hour${n === 1 ? '' : 's'} ago`,
    daysAgo: (n: number) => `${n} day${n === 1 ? '' : 's'} ago`,
    monthsAgo: (n: number) => `${n} month${n === 1 ? '' : 's'} ago`,
    yearsAgo: (n: number) => `${n} year${n === 1 ? '' : 's'} ago`,
  },

  env: {
    staging: 'STAGING environment',
    dev: 'DEV environment',
  },

  shortcuts: {
    title: 'Keyboard shortcuts',
    navigation: 'Navigation',
    nextRow: 'Next row',
    prevRow: 'Previous row',
    select: 'Select',
    selectRange: 'Select range',
    open: 'Open',
    detailPanel: 'Detail panel',
    globalSearch: 'Global search',
    focusFilter: 'Focus filter',
    primaryAction: 'Run primary action',
    escape: 'Close / cancel',
    toggleSidebar: 'Toggle sidebar',
    goHome: 'Go home',
    showShortcuts: 'This list',
  },
}
